import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  Annotation,
  AnnotationColor,
  AnnotationTextColor,
  AnnotationDraft,
  AnnotationFilter,
  ActiveAnnotationSelection,
  AnnotationReference,
  NormalizedContentUnitAnnotationGroup,
} from './annotationTypes';
import {
  ANNOTATION_COLOR_VALUES,
  ANNOTATION_TEXT_COLOR_VALUES,
  createAnnotationFromDraft,
  DEFAULT_ANNOTATION_COLOR,
  DEFAULT_ANNOTATION_TEXT_COLOR,
  isAnnotationColor,
  isAnnotationTextColor,
} from './annotationTypes';
import type { AnnotationService } from './annotationService';

export interface UseAnnotationsParams {
  componentId: string;
  courseId: string;
  initialAnnotations: NormalizedContentUnitAnnotationGroup[];
  service: AnnotationService | null;
  userId?: string | null;
}

export interface AnnotationMutationArgs {
  annotation: Annotation;
  contentUnitId: string;
}

function toAnnotationMap(
  groups: NormalizedContentUnitAnnotationGroup[],
): Record<string, Annotation[]> {
  return groups.reduce<Record<string, Annotation[]>>((map, group) => {
    map[group.contentUnitId] = group.annotations;
    return map;
  }, {});
}

function getErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'Unable to save your annotation right now.';
}

function sortAnnotations(annotations: Annotation[]) {
  return [...annotations].sort((left, right) => left.timestamp - right.timestamp);
}

function updateAnnotationMap(
  previous: Record<string, Annotation[]>,
  contentUnitId: string,
  nextAnnotations: Annotation[],
) {
  return {
    ...previous,
    [contentUnitId]: sortAnnotations(nextAnnotations),
  };
}

function getHighlightColorStorageKey(userId: string | null | undefined) {
  return userId ? `kudu-annotation-highlight-color:${userId}` : null;
}

function getNoteTextColorStorageKey(userId: string | null | undefined) {
  return userId ? `kudu-annotation-note-text-color:${userId}` : null;
}

export function useAnnotations({
  componentId,
  courseId,
  initialAnnotations,
  service,
  userId,
}: UseAnnotationsParams) {
  const initialAnnotationsKey = useMemo(
    () =>
      JSON.stringify(
        initialAnnotations.map((group) => ({
          contentUnitId: group.contentUnitId,
          ids: group.annotations.map((annotation) => annotation.id),
          timestamps: group.annotations.map((annotation) => annotation.timestamp),
        })),
      ),
    [initialAnnotations],
  );
  const [annotationsByContentUnitId, setAnnotationsByContentUnitId] = useState(
    () => toAnnotationMap(initialAnnotations),
  );
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationReference | null>(
    null,
  );
  const [activeSelection, setActiveSelection] =
    useState<ActiveAnnotationSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AnnotationFilter>('all');
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [highlightColor, setHighlightColor] = useState<AnnotationColor>(
    DEFAULT_ANNOTATION_COLOR,
  );
  const [noteTextColor, setNoteTextColor] = useState<AnnotationTextColor>(
    DEFAULT_ANNOTATION_TEXT_COLOR,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storageKey = getHighlightColorStorageKey(userId);

    if (!storageKey) {
      setHighlightColor(DEFAULT_ANNOTATION_COLOR);
      return;
    }

    const storedValue = window.localStorage.getItem(storageKey);
    setHighlightColor(
      storedValue && isAnnotationColor(storedValue)
        ? storedValue
        : DEFAULT_ANNOTATION_COLOR,
    );
  }, [userId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storageKey = getNoteTextColorStorageKey(userId);

    if (!storageKey) {
      setNoteTextColor(DEFAULT_ANNOTATION_TEXT_COLOR);
      return;
    }

    const storedValue = window.localStorage.getItem(storageKey);
    setNoteTextColor(
      storedValue && isAnnotationTextColor(storedValue)
        ? storedValue
        : DEFAULT_ANNOTATION_TEXT_COLOR,
    );
  }, [userId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storageKey = getHighlightColorStorageKey(userId);

    if (!storageKey || !(highlightColor in ANNOTATION_COLOR_VALUES)) {
      return;
    }

    window.localStorage.setItem(storageKey, highlightColor);
  }, [highlightColor, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storageKey = getNoteTextColorStorageKey(userId);

    if (!storageKey || !(noteTextColor in ANNOTATION_TEXT_COLOR_VALUES)) {
      return;
    }

    window.localStorage.setItem(storageKey, noteTextColor);
  }, [noteTextColor, userId]);

  useEffect(() => {
    setAnnotationsByContentUnitId(toAnnotationMap(initialAnnotations));
    setActiveAnnotation(null);
    setActiveSelection(null);
    setError(null);
  }, [componentId, initialAnnotationsKey]);

  const createAnnotation = useCallback(
    async (draft: AnnotationDraft & { contentUnitId: string }) => {
      if (!service) {
        throw new Error('Annotation service unavailable');
      }

      const annotation = createAnnotationFromDraft(draft);
      const previous = annotationsByContentUnitId;
      const nextAnnotations = [
        ...(previous[draft.contentUnitId] ?? []),
        annotation,
      ];

      setError(null);
      setAnnotationsByContentUnitId((current) =>
        updateAnnotationMap(current, draft.contentUnitId, nextAnnotations),
      );
      setActiveSelection(null);

      try {
        await service.createAnnotation({
          annotation,
          componentId,
          contentUnitId: draft.contentUnitId,
          courseId,
        });
        return annotation;
      } catch (mutationError) {
        setAnnotationsByContentUnitId(previous);
        setError(getErrorMessage(mutationError));
        throw mutationError;
      }
    },
    [annotationsByContentUnitId, componentId, courseId, service],
  );

  const updateAnnotation = useCallback(
    async ({ annotation, contentUnitId }: AnnotationMutationArgs) => {
      if (!service) {
        throw new Error('Annotation service unavailable');
      }

      const previous = annotationsByContentUnitId;
      const nextAnnotations = (previous[contentUnitId] ?? []).map((candidate) =>
        candidate.id === annotation.id ? annotation : candidate,
      );

      setError(null);
      setAnnotationsByContentUnitId((current) =>
        updateAnnotationMap(current, contentUnitId, nextAnnotations),
      );

      try {
        await service.updateAnnotation({
          annotation,
          componentId,
          contentUnitId,
          courseId,
        });
      } catch (mutationError) {
        setAnnotationsByContentUnitId(previous);
        setError(getErrorMessage(mutationError));
        throw mutationError;
      }
    },
    [annotationsByContentUnitId, componentId, courseId, service],
  );

  const deleteAnnotation = useCallback(
    async ({ annotation, contentUnitId }: AnnotationMutationArgs) => {
      if (!service) {
        throw new Error('Annotation service unavailable');
      }

      const previous = annotationsByContentUnitId;
      const nextAnnotations = (previous[contentUnitId] ?? []).filter(
        (candidate) => candidate.id !== annotation.id,
      );

      setError(null);
      setAnnotationsByContentUnitId((current) =>
        updateAnnotationMap(current, contentUnitId, nextAnnotations),
      );
      setActiveAnnotation((current) =>
        current?.annotationId === annotation.id ? null : current,
      );
      setActiveSelection((current) =>
        current?.draft.id === annotation.id ? null : current,
      );

      try {
        await service.deleteAnnotation({
          annotation,
          componentId,
          contentUnitId,
          courseId,
        });
      } catch (mutationError) {
        setAnnotationsByContentUnitId(previous);
        setError(getErrorMessage(mutationError));
        throw mutationError;
      }
    },
    [annotationsByContentUnitId, componentId, courseId, service],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const allAnnotations = useMemo(
    () =>
      Object.entries(annotationsByContentUnitId).flatMap(([contentUnitId, annotations]) =>
        annotations.map((annotation) => ({
          annotation,
          contentUnitId,
        })),
      ),
    [annotationsByContentUnitId],
  );

  const filteredAnnotations = useMemo(() => {
    if (filter === 'all') {
      return allAnnotations;
    }

    return allAnnotations.filter(({ annotation }) =>
      filter === 'notes' ? annotation.kind === 'note' : annotation.kind === 'highlight',
    );
  }, [allAnnotations, filter]);

  const getAnnotationsForContentUnit = useCallback(
    (contentUnitId: string) => annotationsByContentUnitId[contentUnitId] ?? [],
    [annotationsByContentUnitId],
  );

  return {
    activeAnnotation,
    activeSelection,
    allAnnotations,
    annotationsByContentUnitId,
    clearError,
    createAnnotation,
    deleteAnnotation,
    error,
    filter,
    filteredAnnotations,
    getAnnotationsForContentUnit,
    highlightColor,
    noteTextColor,
    setActiveAnnotation,
    setActiveSelection,
    setFilter,
    setHighlightColor,
    setNoteTextColor,
    setShowAnnotations,
    showAnnotations,
    updateAnnotation,
  };
}
